import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DollarSign, TrendingUp, Wallet, RefreshCw, ArrowLeft, Calculator, Send, Activity, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM HH:mm'); } catch { return iso; }
};

const RevenueDashboard = () => {
  const navigate = useNavigate();
  const { token, isOverseer, loading: authLoading } = useAuth();

  // Feature-flag state
  const [flags, setFlags] = useState({});
  const [flagsLoading, setFlagsLoading] = useState(true);

  // Pricing
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [pricingResult, setPricingResult] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Wallets
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [topupOp, setTopupOp] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupCurrency, setTopupCurrency] = useState('USD');
  const [payoutOp, setPayoutOp] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutCurrency, setPayoutCurrency] = useState('USD');

  // Refunds
  const [bookingId, setBookingId] = useState('');
  const [refundReason, setRefundReason] = useState('customer');
  const [refundResult, setRefundResult] = useState(null);
  const [refunds, setRefunds] = useState([]);

  // Commission preview
  const [commGross, setCommGross] = useState('');
  const [commResult, setCommResult] = useState(null);

  useEffect(() => {
    if (!authLoading && (!token || !isOverseer())) {
      navigate('/login');
      toast.error('Overseer access required');
    }
  }, [authLoading, token]);

  const loadFlags = async () => {
    setFlagsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/enterprise/feature-flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlags(r.data?.flags || {});
    } catch (e) {
      // non-fatal
    } finally {
      setFlagsLoading(false);
    }
  };

  const toggleFlag = async (flag, enabled) => {
    try {
      await axios.patch(`${API_URL}/enterprise/feature-flags`,
        { flag, enabled }, { headers: { Authorization: `Bearer ${token}` } });
      setFlags(f => ({ ...f, [flag]: enabled }));
      toast.success(`${flag} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle');
    }
  };

  const loadTrips = async () => {
    try {
      const r = await axios.get(`${API_URL}/trips`);
      setTrips(r.data || []);
    } catch (e) { /* ignore */ }
  };

  const loadUsers = async () => {
    try {
      const r = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((r.data || []).filter(u =>
        ['driver', 'conductor', 'station_superuser', 'overseer'].includes(u.role)));
    } catch (e) { /* ignore */ }
  };

  const loadWallets = async () => {
    setWalletsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/enterprise/wallets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWallets(r.data?.wallets || []);
    } catch (e) {
      if (e?.response?.status !== 404) {
        toast.error('Failed to load wallets');
      }
    } finally {
      setWalletsLoading(false);
    }
  };

  const loadRefunds = async () => {
    try {
      const r = await axios.get(`${API_URL}/enterprise/refunds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefunds(r.data?.records || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (token && isOverseer()) {
      loadFlags();
      loadTrips();
      loadUsers();
      loadWallets();
      loadRefunds();
    }
  }, [token]);

  const runPricing = async () => {
    if (!tripId) { toast.error('Select a trip'); return; }
    setPricingLoading(true);
    setPricingResult(null);
    try {
      const r = await axios.post(`${API_URL}/enterprise/pricing/quote`,
        { trip_id: tripId, base_price: basePrice ? parseFloat(basePrice) : undefined, currency: 'USD' },
        { headers: { Authorization: `Bearer ${token}` } });
      setPricingResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pricing failed (is dynamic_pricing enabled?)');
    } finally {
      setPricingLoading(false);
    }
  };

  const runCommission = async () => {
    if (!commGross || parseFloat(commGross) <= 0) { toast.error('Enter gross amount'); return; }
    try {
      const r = await axios.post(`${API_URL}/enterprise/commissions/calculate`,
        { gross_amount: parseFloat(commGross), currency: 'USD' },
        { headers: { Authorization: `Bearer ${token}` } });
      setCommResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commission calc failed');
    }
  };

  const doTopup = async () => {
    if (!topupOp || !topupAmount) { toast.error('Operator + amount required'); return; }
    try {
      await axios.post(`${API_URL}/enterprise/wallets/topup`,
        { operator_id: topupOp, amount: parseFloat(topupAmount), currency: topupCurrency },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Credited ${topupCurrency} ${topupAmount}`);
      setTopupAmount('');
      loadWallets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Topup failed');
    }
  };

  const doPayout = async () => {
    if (!payoutOp || !payoutAmount) { toast.error('Operator + amount required'); return; }
    try {
      await axios.post(`${API_URL}/enterprise/wallets/payout`,
        { operator_id: payoutOp, amount: parseFloat(payoutAmount), currency: payoutCurrency, destination: 'stripe' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Payout ${payoutCurrency} ${payoutAmount} queued (mock Stripe)`);
      setPayoutAmount('');
      loadWallets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payout failed');
    }
  };

  const calcRefund = async (issue = false) => {
    if (!bookingId) { toast.error('Booking ID required'); return; }
    try {
      const endpoint = issue ? 'issue' : 'calculate';
      const r = await axios.post(`${API_URL}/enterprise/refunds/${endpoint}`,
        { booking_id: bookingId, reason: refundReason },
        { headers: { Authorization: `Bearer ${token}` } });
      setRefundResult(r.data);
      if (issue && r.data?.issued) {
        toast.success(`Refund issued: ${r.data.currency} ${r.data.refund_amount}`);
        loadRefunds();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed');
    }
  };

  const userName = (id) => users.find(u => u.id === id)?.full_name || id.slice(0, 8);

  const pricingEnabled = !!flags.dynamic_pricing;
  const walletsEnabled = !!flags.operator_wallets;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="-ml-2 mb-2" data-testid="revenue-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Revenue Engine</h1>
          <p className="text-slate-600">Dynamic pricing · operator wallets · commission splits · smart refunds</p>
        </div>

        {/* Feature flag controls */}
        <Card className="card-panashe">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Activity className="h-4 w-4" /> Module Controls
              {flagsLoading && <RefreshCw className="h-3 w-3 animate-spin text-slate-400" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {['dynamic_pricing', 'operator_wallets', 'auto_wallet_credit', 'stripe_settlements'].map(f => (
                <Button
                  key={f}
                  size="sm"
                  variant={flags[f] ? 'default' : 'outline'}
                  onClick={() => toggleFlag(f, !flags[f])}
                  data-testid={`revenue-flag-${f}`}
                >
                  {flags[f] ? '● ON' : '○ OFF'} · {f.replace(/_/g, ' ')}
                </Button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Flags are in-memory and reset on backend restart. Stripe / EcoCash settlements are MOCKED.
            </p>
          </CardContent>
        </Card>

        <Tabs defaultValue="pricing" className="space-y-4">
          <TabsList data-testid="revenue-tabs">
            <TabsTrigger value="pricing" data-testid="tab-pricing"><TrendingUp className="h-3 w-3 mr-1" /> Pricing</TabsTrigger>
            <TabsTrigger value="wallets" data-testid="tab-wallets"><Wallet className="h-3 w-3 mr-1" /> Wallets</TabsTrigger>
            <TabsTrigger value="refunds" data-testid="tab-refunds"><RefreshCw className="h-3 w-3 mr-1" /> Refunds</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="tab-commissions"><Calculator className="h-3 w-3 mr-1" /> Commission</TabsTrigger>
          </TabsList>

          {/* PRICING TAB */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Dynamic Price Quote</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!pricingEnabled && (
                  <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Enable <code>dynamic_pricing</code> above to compute live quotes.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Trip</Label>
                    <Select value={tripId} onValueChange={setTripId}>
                      <SelectTrigger data-testid="pricing-trip-select"><SelectValue placeholder="Choose trip" /></SelectTrigger>
                      <SelectContent>
                        {trips.slice(0, 50).map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.route_name || t.trip_number || t.id.slice(0, 8)} · {t.departure_datetime?.slice(0, 16) || ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Base Price (USD, optional)</Label>
                    <Input value={basePrice} onChange={e => setBasePrice(e.target.value)} placeholder="e.g. 25" data-testid="pricing-base-input" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={runPricing} disabled={pricingLoading || !pricingEnabled} className="w-full" data-testid="pricing-quote-btn">
                      {pricingLoading ? 'Computing…' : 'Get Quote'}
                    </Button>
                  </div>
                </div>

                {pricingResult && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <p className="text-xs text-slate-500">Base price</p>
                      <p className="text-2xl font-bold">${pricingResult.base_price}</p>
                      <p className="text-xs text-slate-500 mt-2">Recommended price</p>
                      <p className="text-3xl font-bold text-emerald-600">${pricingResult.recommended_price}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Adjustment: <span className={pricingResult.dynamic_factor >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                          {(pricingResult.dynamic_factor * 100).toFixed(1)}%
                        </span> · band ${pricingResult.min_price} – ${pricingResult.max_price}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-white border">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Why this price?</p>
                      {pricingResult.factors.length === 0 ? (
                        <p className="text-xs text-slate-500">No active demand factors — base price is optimal.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {pricingResult.factors.map((f, i) => (
                            <li key={i} className="text-xs flex items-start justify-between gap-2">
                              <span><b>{f.name}</b> · <span className="text-slate-500">{f.reason}</span></span>
                              <Badge variant="outline" className={f.impact >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                {f.impact >= 0 ? '+' : ''}{(f.impact * 100).toFixed(0)}%
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="text-[10px] text-slate-400 mt-3">
                        Occupancy: {pricingResult.context?.occupancy_percent}% · {pricingResult.context?.days_until_departure}d to departure · {pricingResult.context?.seats_sold}/{pricingResult.context?.total_seats} seats
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* WALLETS TAB */}
          <TabsContent value="wallets">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="font-heading text-base">Credit Wallet</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {!walletsEnabled && (
                    <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-700">
                      Enable <code>operator_wallets</code> to use.
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Operator</Label>
                    <Select value={topupOp} onValueChange={setTopupOp}>
                      <SelectTrigger data-testid="topup-operator-select"><SelectValue placeholder="Pick operator" /></SelectTrigger>
                      <SelectContent>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={topupAmount} onChange={e => setTopupAmount(e.target.value)} placeholder="Amount" type="number" data-testid="topup-amount-input" />
                    <Select value={topupCurrency} onValueChange={setTopupCurrency}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="ZAR">ZAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={doTopup} disabled={!walletsEnabled} className="w-full" data-testid="topup-btn">
                    <DollarSign className="h-4 w-4 mr-1" /> Credit
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="font-heading text-base">Payout (mock Stripe)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Operator</Label>
                    <Select value={payoutOp} onValueChange={setPayoutOp}>
                      <SelectTrigger data-testid="payout-operator-select"><SelectValue placeholder="Pick operator" /></SelectTrigger>
                      <SelectContent>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} placeholder="Amount" type="number" data-testid="payout-amount-input" />
                    <Select value={payoutCurrency} onValueChange={setPayoutCurrency}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="ZAR">ZAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={doPayout} disabled={!walletsEnabled} variant="default" className="w-full" data-testid="payout-btn">
                    <Send className="h-4 w-4 mr-1" /> Send Payout
                  </Button>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Wallet className="h-4 w-4" /> Operator Wallets ({wallets.length})
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={loadWallets} disabled={walletsLoading} data-testid="wallets-refresh">
                  <RefreshCw className={`h-3 w-3 ${walletsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent>
                {wallets.length === 0 ? (
                  <p className="text-sm text-slate-500 italic py-4 text-center">No wallets yet. Credit one above to get started.</p>
                ) : (
                  <div className="space-y-2" data-testid="wallets-list">
                    {wallets.map(w => (
                      <div key={w.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`wallet-${w.operator_id}`}>
                        <div>
                          <p className="font-semibold text-sm">{userName(w.operator_id)}</p>
                          <p className="text-xs text-slate-500">
                            Lifetime · in {w.currency} {w.lifetime_credit?.toFixed(2) || '0.00'} · out {w.currency} {w.lifetime_debit?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700 text-base font-mono">
                          {w.currency} {w.balance?.toFixed(2) || '0.00'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* REFUNDS TAB */}
          <TabsContent value="refunds">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Smart Refund Calculator</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Booking ID</Label>
                    <Input value={bookingId} onChange={e => setBookingId(e.target.value)} placeholder="booking uuid" data-testid="refund-booking-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Reason</Label>
                    <Select value={refundReason} onValueChange={setRefundReason}>
                      <SelectTrigger data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer-initiated</SelectItem>
                        <SelectItem value="operational">Operational</SelectItem>
                        <SelectItem value="bus_breakdown">Bus breakdown</SelectItem>
                        <SelectItem value="weather">Weather</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button variant="outline" onClick={() => calcRefund(false)} className="flex-1" data-testid="refund-calc-btn">Preview</Button>
                    <Button onClick={() => calcRefund(true)} className="flex-1" data-testid="refund-issue-btn">Issue</Button>
                  </div>
                </div>

                {refundResult && (
                  <div className="p-4 rounded-lg bg-slate-50 border mt-2" data-testid="refund-result">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold">{refundResult.policy}</p>
                      <Badge className={refundResult.eligible ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                        {refundResult.refund_percent}%
                      </Badge>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {refundResult.currency || 'USD'} {refundResult.refund_amount}
                    </p>
                    <p className="text-xs text-slate-500">
                      Paid: {refundResult.currency || 'USD'} {refundResult.amount_paid} · {refundResult.hours_until_departure?.toFixed(1)}h to departure
                    </p>
                    {refundResult.issued && (
                      <Badge className="mt-2 bg-emerald-600">ISSUED · {refundResult.record?.mock_ref}</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader><CardTitle className="font-heading text-base">Recent Refunds ({refunds.length})</CardTitle></CardHeader>
              <CardContent>
                {refunds.length === 0 ? (
                  <p className="text-sm text-slate-500 italic py-4 text-center">No refunds issued yet.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="refunds-list">
                    {refunds.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded text-sm">
                        <div>
                          <p className="font-mono text-xs text-slate-500">{r.booking_id?.slice(0, 12)}…</p>
                          <p className="text-xs text-slate-600">{r.policy}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-emerald-600">{r.currency} {r.refund_amount}</p>
                          <p className="text-[10px] text-slate-400">{fmtTime(r.issued_at)} · {r.issued_by}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMMISSION TAB */}
          <TabsContent value="commissions">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Commission Split Preview</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">
                  Default policy: operator 70% · driver 5% · station 5% · platform 20%
                </p>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Gross amount (USD)</Label>
                    <Input value={commGross} onChange={e => setCommGross(e.target.value)} placeholder="e.g. 250" type="number" data-testid="commission-input" />
                  </div>
                  <Button onClick={runCommission} data-testid="commission-btn"><Calculator className="h-4 w-4 mr-1" /> Calculate</Button>
                </div>
                {commResult && (
                  <div className="space-y-2 mt-2" data-testid="commission-result">
                    {commResult.splits.map(s => (
                      <div key={s.party} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-semibold capitalize">{s.party}</p>
                          <p className="text-xs text-slate-500">{s.share_percent}% share</p>
                        </div>
                        <p className="text-lg font-bold">${s.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default RevenueDashboard;
